import { supabase } from '@/integrations/supabase/client';

/**
 * Analytics and Telemetry Utility
 * Tracks user events, route views, and interactions
 * 
 * Current implementation: Console logging (can be upgraded to PostHog, Amplitude, etc.)
 * Future: Store in database table for admin analytics dashboard
 */

export interface EventProperties {
  [key: string]: string | number | boolean | undefined | null;
}

export interface RouteViewData {
  route: string;
  userId: string;
  role?: string;
  organizationId?: string;
  timestamp: string;
}

/**
 * Track a custom event
 * @param eventName - Name of the event (e.g., 'login', 'route_view', 'export_click')
 * @param properties - Additional event properties
 */
export const trackEvent = async (eventName: string, properties: EventProperties = {}) => {
  const timestamp = new Date().toISOString();
  
  const eventData = {
    event: eventName,
    properties,
    timestamp,
  };
  
  // Log to console (easily visible in dev tools)
  console.info('[Analytics]', eventName, properties);
  
  // Store in user_events table for analytics dashboard
  try {
    await storeEventInDatabase(eventData);
  } catch (error) {
    console.error('[Analytics] Failed to store event:', error);
  }
};

/**
 * Track route view
 * @param route - Route path (e.g., '/app/dashboard')
 * @param userId - User ID
 * @param role - User's highest role
 * @param organizationId - Organization ID (optional)
 */
export const trackRouteView = (
  route: string,
  userId: string,
  role?: string,
  organizationId?: string
) => {
  console.info('[Analytics] Route View:', route, { userId, role, organizationId });
  
  // Track as custom event with proper typing
  trackEvent('route_view', {
    route,
    userId,
    role,
    organizationId,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track dashboard tile click
 * @param tileName - Name of the tile clicked
 * @param destination - Destination route
 */
export const trackDashboardTileClick = (tileName: string, destination: string) => {
  trackEvent('quick_launch_click', {
    tile: tileName,
    destination,
  });
};

/**
 * Track legacy redirect
 * @param from - Original route
 * @param to - New route
 * @param userId - User ID (optional)
 * @param role - User role (optional)
 */
export const trackLegacyRedirect = (
  from: string,
  to: string,
  userId?: string,
  role?: string
) => {
  console.warn('[Legacy Route]', `${from} → ${to}`);
  
  trackEvent('legacy_redirect', {
    from,
    to,
    userId,
    role,
  });
};

/**
 * Track feature gate interaction (when user sees upgrade prompt)
 * @param feature - Feature name
 * @param action - Action attempted
 * @param tierRequired - Tier required for access
 */
export const trackFeatureGate = (
  feature: string,
  action: string,
  tierRequired?: string
) => {
  trackEvent('feature_gate_view', {
    feature,
    action,
    tierRequired,
  });
};

/**
 * Track order submission
 * @param orderId - Order ID
 * @param totalCost - Total order cost
 * @param itemCount - Number of items
 */
export const trackOrderSubmission = (
  orderId: string,
  totalCost: number,
  itemCount: number
) => {
  trackEvent('order_submitted', {
    orderId,
    totalCost,
    itemCount,
  });
};

/**
 * Track recipe creation/update
 * @param recipeId - Recipe ID
 * @param action - 'create' or 'update'
 * @param ingredientCount - Number of ingredients
 */
export const trackRecipeAction = (
  recipeId: string,
  action: 'create' | 'update' | 'delete',
  ingredientCount?: number
) => {
  trackEvent('recipe_action', {
    recipeId,
    action,
    ingredientCount,
  });
};

/**
 * Track menu planning action
 * @param menuId - Menu ID
 * @param action - Action type
 * @param mealCount - Number of meals
 */
export const trackMenuAction = (
  menuId: string,
  action: 'create' | 'update' | 'delete' | 'submit',
  mealCount?: number
) => {
  trackEvent('menu_action', {
    menuId,
    action,
    mealCount,
  });
};

/**
 * Store event in user_events table for analytics
 * Tracks user activity across the platform
 */
const storeEventInDatabase = async (eventData: any) => {
  // Define which events to store in user_events table
  const trackedEvents = [
    'login',
    'route_view',
    'export_click',
    'feature_gate_view',
    'chart_interaction',
  ];
  
  if (!trackedEvents.includes(eventData.event)) {
    return; // Skip non-tracked events
  }
  
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return;
    
    // Get user's organization
    const { data: profile } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', user.id)
      .single();
    
    if (!profile?.organization_id) return;
    
    // Insert into user_events table (use type assertion until types are regenerated)
    const { error } = await (supabase as any).from('user_events').insert({
      organization_id: profile.organization_id,
      user_id: user.id,
      event: eventData.event,
      context: eventData.properties || {}
    });
    
    if (error) {
      console.error('[Analytics] Insert error:', error);
    }
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('[Analytics] Database storage failed:', error);
  }
};

/**
 * Debounce function for route view tracking
 * Prevents excessive tracking on rapid navigation
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};
